import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import cloneDeep from 'lodash.clonedeep'
import { Box } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { ProjectGridField as TSField } from '@/types/fields'
import { ProjectGrid } from '@/types/projects'
import useLocalStorage from '@/hooks/useLocalStorage'
import useContentCategories from '@/services/hooks/useContentCategories'
import { useGetDocument } from '@/service-library/hooks/documents'
import { useGetProjectGrids } from '@/service-library/hooks/project-grids'
import {
  appendContentCategoriesToGrids,
  appendSubGridFieldsToBaseGridAndCleanFields,
  getBaseGrid,
  sortBySortOrder,
} from '@/utils/field'
import DocumentComparisonDataProvider from '@/components/image-zoom-pan/providers/DocumentComparisonDataProvider'
import useDocumentValidationSubscribe from '@/components/notifications/useDocumentValidationSubscribe'
import DocumentProvider from '@/components/image-zoom-pan/providers/DocumentProvider'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import ComparisonViewDocument from './ComparisonViewDocument'
import DocumentRowValuesProvider from './providers/DocumentRowValuesProvider'
import DocumentChipsProvider from './providers/DocumentChipsProvider'
import DocumentRowsProvider from './providers/DocumentRowsProvider'
import useDocumentComparisonData from './useDocumentComparisonData'
import DocumentComparisonSelection from './DocumentComparisonSelection'

type ComparisonViewProps = {
  documentQuery: ReturnType<typeof useGetDocument>
}
export type ComparisonModeOptions = 'image' | 'all_fields' | 'relevant_fields'

export default function ComparisonView({ documentQuery }: ComparisonViewProps) {
  const { projectId } = useParams()

  const { document } = documentQuery
  const [selectedComparisonMode] = useLocalStorage<ComparisonModeOptions>(
    'comparison-selected-mode',
    'image',
  )

  const [documentIdToCompare, setDocumentIdToCompare] = useState<string>('')
  const [compareDocumentEnabled, setCompareDocumentEnabled] = useState(false)

  const { project } = useProjectContext()
  const { selectedWorkflow } = useSelectedWorkflowContext()

  const comparisonData = useDocumentComparisonData({
    enabled: compareDocumentEnabled,
    mainDocument: document,
    secondaryDocumentId: documentIdToCompare,
    mode: selectedComparisonMode,
  })

  const {
    secondaryDocumentQuery,
    secondaryDocumentChipsQuery,
    secondaryDocumentRowValuesQuery,
    secondaryDocumentRowsQuery,
  } = comparisonData

  // Using a ref so that we keep the old data while the new one is loading
  const loadedSecondaryDocumentQueyRef = useRef(secondaryDocumentQuery)

  const { projectGrids = [] } = useGetProjectGrids({
    filters: {
      limit: '1000',
      project_id: projectId,
      fields__include: 'project_grid_fields',
      project_grid_fields__fields__include:
        'project_grid_field_type,project_grid_field_rules',
      project_grid_fields__project_grid_field_rules__fields__include: 'rule',
    },
    initialListData: project?.project_grids,
  })

  const { categoriesContent = [], isPending: categoriesIsLoading } =
    useContentCategories({
      projectId,
      refetchOnWindowFocus: true,
    })

  const workflowStates = selectedWorkflow?.workflow_states || []

  const workflowState = workflowStates.find((state) =>
    document?.workflow_states_ids?.some((id) => id === state.id),
  )

  const fields: TSField[] = useMemo(() => {
    if (projectGrids.length > 0 && !categoriesIsLoading) {
      const newGrids =
        categoriesContent.length > 0
          ? appendContentCategoriesToGrids(
              cloneDeep(projectGrids),
              categoriesContent,
            )
          : cloneDeep(projectGrids)
      const baseGrid = getBaseGrid(newGrids) as ProjectGrid
      const fullBaseGrid = appendSubGridFieldsToBaseGridAndCleanFields({
        baseGrid,
        grids: newGrids,
      })
      //TODO: Remove sorting for when the backend returns data sorted
      return sortBySortOrder(fullBaseGrid.project_grid_fields)
    }
    return []
  }, [categoriesContent, categoriesIsLoading, projectGrids])

  const groupedFields = useMemo(() => {
    return selectedComparisonMode === 'image'
      ? []
      : fields.reduce<TSField[][]>((acc, field) => {
          let isVisible = false
          switch (selectedComparisonMode) {
            case 'relevant_fields':
              // We don't check for children fields here because if all of them are excluded,
              // the parent field id will be excluded as well
              isVisible =
                !workflowState?.excluded_project_grid_fields_ids?.includes(
                  field.id,
                )
              break
            case 'all_fields':
              isVisible = true
              break
          }

          if (isVisible) {
            if (field.fields) {
              const tableFields =
                // :flagged: if the parent field is visible when this option is selected, it means
                // at least one of its children is visible, so we show all of them
                selectedComparisonMode === 'all_fields'
                  ? field.fields
                  : field.fields.filter(
                      (tableField) =>
                        !workflowState?.excluded_project_grid_fields_ids?.includes(
                          tableField.id,
                        ),
                    )
              acc.push([{ ...field, fields: tableFields }])
              acc.push([])
            } else {
              if (acc.length === 0) {
                acc.push([])
              }
              acc[acc.length - 1].push(field)
            }
          }

          return acc
        }, [])
  }, [
    fields,
    selectedComparisonMode,
    workflowState?.excluded_project_grid_fields_ids,
  ])

  useEffect(() => {
    if (
      secondaryDocumentQuery.document?.id &&
      secondaryDocumentQuery.document?.id !==
        loadedSecondaryDocumentQueyRef.current.document?.id
    ) {
      loadedSecondaryDocumentQueyRef.current = secondaryDocumentQuery
    }
  }, [secondaryDocumentQuery])

  useDocumentValidationSubscribe({
    documentId: loadedSecondaryDocumentQueyRef.current.document?.id,
    documentQueryKey: secondaryDocumentQuery.queryKey,
    chipsListQueryKey: secondaryDocumentChipsQuery.queryKey,
    rowsListQueryKey: secondaryDocumentRowsQuery.queryKey,
    rowValuesListQueryKey: secondaryDocumentRowValuesQuery.queryKey,
  })

  if (!document) return null

  const commonProps = {
    groupedFields,
    mode: selectedComparisonMode,
    mainDocument: document,
  }

  return (
    <DocumentComparisonDataProvider
      compareDocumentEnabled={compareDocumentEnabled}
      documentIdToCompare={documentIdToCompare}
      setCompareDocumentEnabled={setCompareDocumentEnabled}
      setDocumentIdToCompare={setDocumentIdToCompare}
      {...comparisonData}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid
          container
          sx={{
            flexGrow: 1,
            minHeight: 0,
            minWidth: 0,
            overflowY: 'auto', // This is needed to make the grid scrollable
          }}
        >
          <Grid
            size={6}
            sx={{
              borderRight: (theme) => `1px solid ${theme.palette.divider}`,
              display: 'flex',
              minHeight: 0,
              minWidth: 0,
              height: '100%',
              position: 'relative',
            }}
          >
            {loadedSecondaryDocumentQueyRef.current.document ? (
              <DocumentProvider
                query={loadedSecondaryDocumentQueyRef.current}
                isDisabled
              >
                <DocumentRowsProvider query={secondaryDocumentRowsQuery}>
                  <DocumentRowValuesProvider
                    query={secondaryDocumentRowValuesQuery}
                  >
                    <DocumentChipsProvider
                      documentId={
                        loadedSecondaryDocumentQueyRef.current.document.id
                      }
                      query={secondaryDocumentChipsQuery}
                    >
                      <ComparisonViewDocument
                        side="left"
                        documentQuery={loadedSecondaryDocumentQueyRef.current}
                        {...commonProps}
                      />
                    </DocumentChipsProvider>
                  </DocumentRowValuesProvider>
                </DocumentRowsProvider>
              </DocumentProvider>
            ) : (
              <Box sx={{ margin: 'auto', width: '80%' }}>
                <DocumentComparisonSelection
                  document={document}
                  loadIfOnlyOneSuggestion
                />
              </Box>
            )}
          </Grid>
          <Grid
            size={6}
            sx={{
              display: 'flex',
              minHeight: 0,
              minWidth: 0,
              height: '100%',
              justifyContent: 'center',
            }}
          >
            <ComparisonViewDocument
              side="right"
              documentQuery={documentQuery}
              {...commonProps}
            />
          </Grid>
        </Grid>
      </LocalizationProvider>
    </DocumentComparisonDataProvider>
  )
}
